import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RotateCcw, Package, Clock, CheckCircle, XCircle, AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";

const Returns = () => {
  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="flex items-center gap-3 mb-8">
        <RotateCcw className="h-8 w-8 text-primary" />
        <h1 className="text-3xl font-bold">Returns & Exchanges</h1>
      </div>
      
      <div className="space-y-6">
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Return Policy Overview</h2>
          <p className="text-muted-foreground mb-4">
            We want you to be completely satisfied with your purchase. If you're not happy with your order, you can return most items within 7 days of delivery for a full refund or exchange.
          </p>
          <div className="grid md:grid-cols-3 gap-4 mt-6">
            <div className="text-center">
              <Clock className="h-8 w-8 text-primary mx-auto mb-2" />
              <p className="font-medium">7 Days</p>
              <p className="text-sm text-muted-foreground">Return window</p>
            </div>
            <div className="text-center">
              <Package className="h-8 w-8 text-primary mx-auto mb-2" />
              <p className="font-medium">Original Packaging</p>
              <p className="text-sm text-muted-foreground">Required for returns</p>
            </div>
            <div className="text-center">
              <CheckCircle className="h-8 w-8 text-primary mx-auto mb-2" />
              <p className="font-medium">Full Refund</p>
              <p className="text-sm text-muted-foreground">On approved returns</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Eligible Items</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-medium mb-3 flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                Returnable Items
              </h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>• Unopened electronics</li>
                <li>• Unworn clothing with tags</li>
                <li>• Unused beauty products</li>
                <li>• Books in original condition</li>
                <li>• Home & kitchen items</li>
                <li>• Toys and games</li>
              </ul>
            </div>
            <div>
              <h3 className="font-medium mb-3 flex items-center gap-2">
                <XCircle className="h-5 w-5 text-red-500" />
                Non-Returnable Items
              </h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>• Perishable goods</li>
                <li>• Intimate or sanitary products</li>
                <li>• Hazardous materials</li>
                <li>• Customized/personalized items</li>
                <li>• Digital products</li>
                <li>• Gift cards</li>
              </ul>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">How to Return an Item</h2>
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">1</div>
              <div>
                <h3 className="font-medium mb-1">Initiate Return</h3>
                <p className="text-sm text-muted-foreground">
                  Log into your account and go to "My Orders". Select the item you want to return and click "Return Item".
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">2</div>
              <div>
                <h3 className="font-medium mb-1">Pack the Item</h3>
                <p className="text-sm text-muted-foreground">
                  Pack the item securely in its original packaging with all accessories, manuals, and documents.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">3</div>
              <div>
                <h3 className="font-medium mb-1">Schedule Pickup</h3>
                <p className="text-sm text-muted-foreground">
                  We'll arrange a pickup from your address or you can drop it off at our designated locations.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">4</div>
              <div>
                <h3 className="font-medium mb-1">Receive Refund</h3>
                <p className="text-sm text-muted-foreground">
                  Once we receive and inspect the item, your refund will be processed within 5-7 business days.
                </p>
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-6 border-yellow-200 bg-yellow-50">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-yellow-600" />
            Important Notes
          </h2>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li>• Items must be returned in their original condition</li>
            <li>• Refunds will be credited to the original payment method</li>
            <li>• Shipping fees are non-refundable unless the return is due to our error</li>
            <li>• Damaged or defective items may be eligible for immediate replacement</li>
            <li>• For exchanges, the new item will be shipped after we receive the returned item</li>
          </ul>
        </Card>

        <div className="text-center">
          <p className="text-muted-foreground mb-4">
            Have questions about returns?
          </p>
          <Link to="/contact">
            <Button>Contact Customer Support</Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Returns;